import { useState, useRef } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/query-client";

interface CompanyContactOption {
  id: string;
  firstName: string | null;  // company name stored here
  phone: string | null;
  email: string | null;
}

interface CompanyContactComboboxProps {
  value?: string | null;
  onSelect: (company: CompanyContactOption | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
}

export function CompanyContactCombobox({
  value,
  onSelect,
  placeholder = "Search companies...",
  allowCreate = false,
}: CompanyContactComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const queryClient = useQueryClient();

  // Track the resolved selected company across search key changes.
  // isPending becomes true every time `search` changes (new cache key), so
  // we must not use `isPending && value` to decide what to show in the trigger
  // while the user is actively searching — that would flash "Loading..." on
  // every keystroke. Instead we keep the last-resolved selection in a ref and
  // only show the loading state before we have ever fetched data for this value.
  const resolvedSelectedRef = useRef<CompanyContactOption | null>(null);
  const initialLoadDoneRef = useRef(false);

  const { data, isPending } = useQuery({
    queryKey: ["/api/contacts", "company-combobox", search],
    queryFn: async () => {
      const params = new URLSearchParams({ entityType: "company", limit: "30" });
      if (search) params.set("search", search);
      const res = await apiRequest("GET", `/api/contacts?${params}`);
      return res.json() as Promise<{ contacts: CompanyContactOption[] }>;
    },
  });

  // When a value (companyId) is pre-set but not found in the current search
  // results (e.g. the company is outside the first 30), fetch it directly so
  // the trigger always displays the correct company name.
  const { data: selectedContactData } = useQuery({
    queryKey: ["/api/contacts", "company-combobox-selected", value],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/contacts/${value}`);
      return res.json() as Promise<CompanyContactOption>;
    },
    enabled: !!value,
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/contacts", {
        firstName: name,
        lastName: "",
        contactType: "contractor",
        entityType: "company",
      });
      return res.json() as Promise<CompanyContactOption>;
    },
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      onSelect(company);
      setCreateOpen(false);
      setOpen(false);
      setNewName("");
    },
  });

  const options = data?.contacts ?? [];
  const selectedFromOptions = options.find((c) => c.id === value) ?? null;

  // Once data arrives, update the persisted ref so the trigger always shows
  // the real company name — even when the search key changes and isPending
  // becomes true again for the new search query.
  if (data !== undefined) {
    initialLoadDoneRef.current = true;
    if (selectedFromOptions) {
      resolvedSelectedRef.current = selectedFromOptions;
    } else if (selectedContactData && value && selectedContactData.id === value) {
      // Company is not in the first 30 results — use the directly-fetched contact.
      resolvedSelectedRef.current = selectedContactData;
    } else if (!value) {
      resolvedSelectedRef.current = null;
    }
  }
  // If value was cleared externally, reset the ref.
  if (!value && resolvedSelectedRef.current) {
    resolvedSelectedRef.current = null;
  }

  const selected = selectedFromOptions ?? resolvedSelectedRef.current ?? (selectedContactData && value && selectedContactData.id === value ? selectedContactData : null);

  // Show loading only on the very first load (before any data has arrived)
  // when a value is already set — not on subsequent search re-queries.
  const showLoading = isPending && value && !initialLoadDoneRef.current;

  const getLabel = (c: CompanyContactOption) => c.firstName || "Unnamed Company";
  const exactMatch = options.some((c) => getLabel(c).toLowerCase() === search.trim().toLowerCase());
  const canCreate = allowCreate && search.trim().length > 0 && !exactMatch;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
            <span className={cn("truncate min-w-0", (!selected && !showLoading) && "text-muted-foreground")}>
              {showLoading
                ? "Loading..."
                : selected
                  ? getLabel(selected)
                  : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search by name..." value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty>
                {canCreate ? (
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 text-sm w-full hover:bg-accent"
                    onClick={() => { setNewName(search.trim()); setOpen(false); setCreateOpen(true); }}
                  >
                    <Plus className="h-4 w-4" />
                    Add &quot;{search.trim()}&quot;
                  </button>
                ) : "No companies found."}
              </CommandEmpty>
              <CommandGroup>
                {selected && (
                  <CommandItem value="__clear__" onSelect={() => { onSelect(null); setOpen(false); setSearch(""); }}>
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="text-muted-foreground">Clear selection</span>
                  </CommandItem>
                )}
                {options.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={getLabel(company)}
                    onSelect={() => { onSelect(company); setOpen(false); setSearch(""); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", company.id === value ? "opacity-100" : "opacity-0")} />
                    {getLabel(company)}
                  </CommandItem>
                ))}
                {canCreate && options.length > 0 && (
                  <CommandItem
                    value={`__create__${search}`}
                    onSelect={() => { setNewName(search.trim()); setOpen(false); setCreateOpen(true); }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add &quot;{search.trim()}&quot;
                  </CommandItem>
                )}
                {allowCreate && !canCreate && (
                  <CommandItem value="__create_new__" onSelect={() => { setOpen(false); setCreateOpen(true); }}>
                    <Plus className="mr-2 h-4 w-4" />
                    <span>New company...</span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {allowCreate && (
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[360px]">
            <DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-2">
              <div>
                <Label>Company Name *</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => createMutation.mutate(newName)}
                  disabled={createMutation.isPending || !newName.trim()}
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
